'use client';

import { useState, useEffect } from 'react';
import BookingForm from './BookingForm';
import BookingSummary from './BookingSummary';
import { trackEvent, trackBookingStarted } from '@/lib/analytics';
import Icon from '@/components/ui/AppIcon';
import { saveAbandonedBooking, clearAbandonedBooking } from '@/lib/abandonedBooking';
import { saveNotificationToStorage } from '@/lib/whatsappNotification';

const BookingInteractive = () => {
  const [bookingDetails, setBookingDetails] = useState<any>(null);
  const [bookingType, setBookingType] = useState<'flight' | 'hotel' | 'holiday'>('flight');
  const [showPayment, setShowPayment] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [submittedFormData, setSubmittedFormData] = useState<any>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [bookingRef, setBookingRef] = useState('');

  useEffect(() => {
    // Get booking details from URL params
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type') as 'flight' | 'hotel' | 'holiday';
    
    if (type) {
      setBookingType(type);
      const details: any = { type };
      
      // Extract all params
      params.forEach((value, key) => {
        if (key === 'passengers') {
          try {
            details[key] = JSON.parse(value);
          } catch {
            details[key] = value;
          }
        } else {
          details[key] = value;
        }
      });
      
      setBookingDetails(details);
      
      trackEvent('booking_page_view', {
        booking_type: type,
        price: details.price,
      });
      trackBookingStarted({
        booking_type: type,
        destination: details.to || details.destination,
        price: parseFloat(details.price || 0),
      });
    }
  }, []);

  const handleBookingSubmit = (formData: any) => {
    trackEvent('booking_form_submitted', {
      booking_type: bookingType,
      ...formData,
    });
    trackEvent('add_payment_info', {
      currency: 'GBP',
      value: parseFloat(bookingDetails?.price || 0),
      payment_type: formData.paymentMethod || 'card',
    });
    setSubmittedFormData(formData);
    // Save as abandoned at payment step
    saveAbandonedBooking({
      step: 'payment',
      formData,
      bookingDetails,
      bookingType,
    });
    setShowPayment(true);
  };

  const handleConfirmPayment = () => {
    setProcessingPayment(true);
    const ref = `DFF-${Date.now().toString().slice(-8).toUpperCase()}`;
    const pnrChars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const pnr = Array.from({ length: 6 }, () => pnrChars[Math.floor(Math.random() * pnrChars.length)]).join('');
    setBookingRef(ref);

    setTimeout(() => {
      setProcessingPayment(false);
      setBookingComplete(true);

      const basePrice = parseFloat(bookingDetails?.price || 0);
      const serviceFee = bookingType === 'flight' ? 15 : bookingType === 'hotel' ? 20 : 50;
      const totalPrice = basePrice + serviceFee;

      // Save to localStorage for lead dashboard
      const bookings = JSON.parse(localStorage.getItem('bookingLeads') || '[]');
      bookings.unshift({
        id: Date.now().toString(),
        name: `${submittedFormData.firstName} ${submittedFormData.lastName}`,
        email: submittedFormData.email,
        phone: submittedFormData.phone,
        whatsapp: submittedFormData.phone,
        message: submittedFormData.specialRequests || 'Direct booking',
        bookingDetails: {
          type: bookingType,
          ...bookingDetails,
        },
        timestamp: new Date().toISOString(),
        status: 'booked',
        bookingType: 'direct',
        paymentStatus: 'paid',
        paymentMethod: submittedFormData.paymentMethod,
        bookingRef: ref,
        pnr,
        totalPrice,
        basePrice,
        serviceFee,
      });
      localStorage.setItem('bookingLeads', JSON.stringify(bookings));

      // Save notification to localStorage
      saveNotificationToStorage({
        type: 'booking_confirmed',
        name: `${submittedFormData.firstName} ${submittedFormData.lastName}`,
        phone: submittedFormData.phone,
        email: submittedFormData.email,
        destination: bookingDetails?.to || bookingDetails?.destination,
        bookingRef: ref,
        pnr,
        totalPrice,
      });

      // Clear abandoned booking since it's now complete
      clearAbandonedBooking('');

      trackEvent('booking_payment_completed', {
        booking_type: bookingType,
        price: bookingDetails?.price,
      });

      // Redirect to confirmation page
      window.location.href = `/booking-confirmation?ref=${ref}`;
    }, 1800);
  };

  if (!bookingDetails) {
    return (
      <div className="text-center py-12">
        <Icon name="ExclamationTriangleIcon" size={48} className="mx-auto text-warning mb-4" />
        <h2 className="font-heading font-bold text-2xl text-foreground mb-2">
          No Booking Details Found
        </h2>
        <p className="text-muted-foreground mb-6">
          Please select a flight, hotel, or holiday package to continue.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <button
            onClick={() => window.location.href = '/flight-search'}
            className="bg-gradient-to-r from-primary to-secondary text-white font-bold px-6 py-3 rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
          >
            <Icon name="PaperAirplaneIcon" size={18} />
            Search Flights
          </button>
          <button
            onClick={() => window.location.href = '/hotels'}
            className="border-2 border-primary text-primary font-bold px-6 py-3 rounded-lg hover:bg-primary hover:text-white transition-all flex items-center gap-2"
          >
            <Icon name="BuildingOffice2Icon" size={18} />
            Search Hotels
          </button>
        </div>
      </div>
    );
  }

  if (bookingComplete) {
    const basePrice = parseFloat(bookingDetails?.price || 0);
    const serviceFee = bookingType === 'flight' ? 15 : bookingType === 'hotel' ? 20 : 50;
    const totalPrice = basePrice + serviceFee;
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-green-500/30 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircleIcon" size={48} className="text-green-600" />
          </div>
          <h2 className="font-heading font-bold text-3xl text-foreground mb-2">
            Booking Confirmed!
          </h2>
          <p className="text-muted-foreground mb-2">
            Your booking reference is:
          </p>
          <p className="text-2xl font-black text-primary mb-6 tracking-widest">{bookingRef}</p>

          {/* Booking Summary Card */}
          <div className="bg-surface rounded-xl p-5 mb-6 text-left space-y-2 text-sm">
            {bookingDetails.airline && (
              <div className="flex justify-between">
                <span className="text-muted-foreground font-bold">Airline</span>
                <span className="font-black text-foreground">{bookingDetails.airline} · {bookingDetails.flightNumber}</span>
              </div>
            )}
            {bookingDetails.from && bookingDetails.to && (
              <div className="flex justify-between">
                <span className="text-muted-foreground font-bold">Route</span>
                <span className="font-black text-foreground">{bookingDetails.from} → {bookingDetails.to}</span>
              </div>
            )}
            {bookingDetails.departDate && (
              <div className="flex justify-between">
                <span className="text-muted-foreground font-bold">Departure</span>
                <span className="font-black text-foreground">{bookingDetails.departDate}{bookingDetails.departTime ? ` at ${bookingDetails.departTime}` : ''}</span>
              </div>
            )}
            {bookingDetails.cabinClass && (
              <div className="flex justify-between">
                <span className="text-muted-foreground font-bold">Cabin</span>
                <span className="font-black text-foreground capitalize">{bookingDetails.cabinClass.replace('-', ' ')}</span>
              </div>
            )}
            <div className="border-t border-border pt-2 flex justify-between">
              <span className="font-bold text-foreground">Total Paid</span>
              <span className="font-black text-primary text-lg">£{totalPrice.toFixed(2)}</span>
            </div>
          </div>

          <p className="text-muted-foreground mb-6 text-sm">
            A confirmation email will be sent to <strong>{submittedFormData?.email}</strong>. Our team will contact you shortly to finalise your reservation.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => window.location.href = '/booking-management'}
              className="bg-gradient-to-r from-primary to-secondary text-white font-bold px-6 py-3 rounded-lg hover:shadow-lg transition-all"
            >
              View My Bookings
            </button>
            <button
              onClick={() => window.location.href = '/homepage'}
              className="border-2 border-primary text-primary font-bold px-6 py-3 rounded-lg hover:bg-primary hover:text-white transition-all"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showPayment) {
    const basePrice = parseFloat(bookingDetails?.price || 0);
    const serviceFee = bookingType === 'flight' ? 15 : bookingType === 'hotel' ? 20 : 50;
    const totalPrice = basePrice + serviceFee;
    const paymentMethodLabels: Record<string, string> = {
      card: 'Credit / Debit Card',
      paypal: 'PayPal',
      bank: 'Bank Transfer',
    };

    if (processingPayment) {
      return (
        <div className="text-center py-16">
          <div className="animate-spin w-16 h-16 border-4 border-primary border-t-transparent rounded-full mx-auto mb-6"></div>
          <h2 className="font-heading font-bold text-2xl text-foreground mb-2">
            Processing Your Payment...
          </h2>
          <p className="text-muted-foreground">Please wait while we confirm your reservation.</p>
        </div>
      );
    }

    return (
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Payment Review */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-accent/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="CreditCardIcon" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-black text-2xl text-foreground">Review & Pay</h2>
              <p className="text-sm text-muted-foreground">Confirm your booking details before payment</p>
            </div>
          </div>

          {/* Passenger Details */}
          <div className="bg-surface rounded-xl p-5 mb-5">
            <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
              <Icon name="UserIcon" size={18} className="text-primary" />
              Passenger Details
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Name</p>
                <p className="font-black text-foreground">{submittedFormData?.firstName} {submittedFormData?.lastName}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Email</p>
                <p className="font-black text-foreground">{submittedFormData?.email}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Phone</p>
                <p className="font-black text-foreground">{submittedFormData?.phone}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Payment Method</p>
                <p className="font-black text-foreground">{paymentMethodLabels[submittedFormData?.paymentMethod] || submittedFormData?.paymentMethod}</p>
              </div>
            </div>
          </div>

          {/* Flight Details */}
          <div className="bg-surface rounded-xl p-5 mb-5">
            <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
              <Icon name="PaperAirplaneIcon" size={18} className="text-primary" />
              Flight Details
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {bookingDetails.airline && (
                <div>
                  <p className="text-muted-foreground">Airline</p>
                  <p className="font-black text-foreground">{bookingDetails.airline}</p>
                </div>
              )}
              {bookingDetails.flightNumber && (
                <div>
                  <p className="text-muted-foreground">Flight No.</p>
                  <p className="font-black text-foreground">{bookingDetails.flightNumber}</p>
                </div>
              )}
              {bookingDetails.from && (
                <div>
                  <p className="text-muted-foreground">From</p>
                  <p className="font-black text-foreground">{bookingDetails.from}</p>
                </div>
              )}
              {bookingDetails.to && (
                <div>
                  <p className="text-muted-foreground">To</p>
                  <p className="font-black text-foreground">{bookingDetails.to}</p>
                </div>
              )}
              {bookingDetails.departDate && (
                <div>
                  <p className="text-muted-foreground">Departure</p>
                  <p className="font-black text-foreground">{bookingDetails.departDate}{bookingDetails.departTime ? ` · ${bookingDetails.departTime}` : ''}</p>
                </div>
              )}
              {bookingDetails.returnDate && (
                <div>
                  <p className="text-muted-foreground">Return</p>
                  <p className="font-black text-foreground">{bookingDetails.returnDate}</p>
                </div>
              )}
              {bookingDetails.cabinClass && (
                <div>
                  <p className="text-muted-foreground">Cabin Class</p>
                  <p className="font-black text-foreground capitalize">{bookingDetails.cabinClass.replace('-', ' ')}</p>
                </div>
              )}
              {bookingDetails.duration && (
                <div>
                  <p className="text-muted-foreground">Duration</p>
                  <p className="font-black text-foreground">{bookingDetails.duration}</p>
                </div>
              )}
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-foreground mb-3">Price Breakdown</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-foreground">
                <span>Base Price</span>
                <span className="font-bold">£{basePrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-foreground">
                <span>Service Fee</span>
                <span className="font-bold">£{serviceFee.toFixed(2)}</span>
              </div>
              <div className="border-t border-primary/20 pt-2 flex justify-between text-lg font-black text-primary">
                <span>Total</span>
                <span>£{totalPrice.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowPayment(false)}
              className="flex-1 border-2 border-border text-foreground font-bold py-3 rounded-xl hover:border-primary transition-colors"
            >
              ← Edit Details
            </button>
            <button
              onClick={handleConfirmPayment}
              className="flex-1 bg-gradient-to-r from-primary to-secondary text-white font-heading font-black py-3 rounded-xl hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Icon name="LockClosedIcon" size={18} />
              Confirm & Pay £{totalPrice.toFixed(2)}
            </button>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-3">
            <Icon name="ShieldCheckIcon" size={14} className="inline mr-1" />
            Secure payment processing. Your information is protected.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <BookingForm
          bookingDetails={bookingDetails}
          onSubmit={handleBookingSubmit}
        />
      </div>
      <div className="lg:col-span-1">
        <BookingSummary bookingDetails={bookingDetails} />
      </div>
    </div>
  );
};

export default BookingInteractive;
